/* eslint-disable @typescript-eslint/naming-convention -- API response types use snake_case */
import { PatientDocumentsSummary } from '@sondermind/utilities/models-documents';
import { EventType } from '@sondermind/utilities/models-scheduling';
import { VideoVendor } from '@sondermind/utilities/models-session';

export interface IQuestionnaireScore {
  name: string;
  score: string;
  suicide_warning: boolean;
}

/** Backend may expose tag as flat key, nested object, or string session_duration_type. */
export function extractSessionDurationTypeTagFromScheduledEventJson(json: {
  session_duration_type_tag?: string;
  session_duration_type?: string | { tag?: string };
}): string | undefined {
  if (json.session_duration_type_tag) {
    return json.session_duration_type_tag;
  }
  const nested = json.session_duration_type;
  if (nested == null) {
    return undefined;
  }
  if (typeof nested === 'string') {
    return nested;
  }
  if (typeof nested === 'object' && nested !== null && 'tag' in nested) {
    const { tag } = nested as { tag?: string };
    return typeof tag === 'string' ? tag : undefined;
  }
  return undefined;
}

export class ScheduledEvent {
  // questionnaire based scores and risk tag related data
  questionnaireScores: IQuestionnaireScore[];
  patientIsNonSpnPatient: boolean;
  patientAccountPending: boolean;
  documents: PatientDocumentsSummary;
  endTime: Date;
  id: number;
  createdAt: Date;
  patientFirstName: string;
  patientFullName: string;
  patientDisplayName: string;
  patientId: number;
  referralMemberId?: number;
  clientPlatformIdentity?: string;
  patientInitials: string;
  patientDOB: string;
  personaClientId: number;
  personaProviderId: number;
  providerFirstName: string;
  providerFullName: string;
  providerDisplayName: string;
  providerId: number;
  providerInitials: string;
  providerProfilePictureUrl: string;
  sessionType: string;
  startTime: Date;
  videoEnabled: boolean;
  eventType: string;
  eventState: string;
  proposalStatus: string;
  locationId: number;
  locationName: string;
  title: string;
  userId: number;
  disallowClaims: boolean;
  canceled: boolean;
  referralMemberState: string;
  videoVendor: VideoVendor;
  isFirst: boolean;
  riskPriority: number | null;

  recurring?: boolean;
  recurringScheduleId?: number;
  recurringType?: string;
  recurringMonday?: boolean;
  recurringTuesday?: boolean;
  recurringWednesday?: boolean;
  recurringThursday?: boolean;
  recurringFriday?: boolean;
  recurringSaturday?: boolean;
  recurringSunday?: boolean;
  recurringInterval?: number;
  recurringOrdinal?: string;
  recurringEnd?: string;
  transcriptionRevokedConsentAt?: string;
  providerTranscriptionConsentAt?: string;
  clientTranscriptionConsentAt?: string;
  providerTranscriptionRevokedConsentAt?: string;
  clientTranscriptionRevokedConsentAt?: string;
  bothInSessionTime?: number;
  sessionOverlapStartedAt?: string;
  sessionOverlapEndedAt?: string;
  /** Session duration type tag when variable session duration applies (from API). */
  sessionDurationTypeTag?: string;

  constructor(json: ScheduledEventResponse) {
    this.questionnaireScores = json.questionnaire_scores;
    this.endTime = this.addOneSecond(json.end_time);
    this.id = json.id;
    this.createdAt = new Date(json.created_at);
    this.patientFirstName = json.patient_first_name;
    this.patientFullName = json.patient_full_name;
    this.patientDisplayName = json.patient_display_name;
    this.patientId = json.patient_id;
    this.referralMemberId = json.referral_member_id;
    this.clientPlatformIdentity = json.client_platform_identity;
    this.patientInitials = json.patient_initials;
    this.patientDOB = json.patient_dob;
    this.personaClientId = json.persona_client_id;
    this.personaProviderId = json.persona_provider_id;
    this.providerFirstName = json.provider_first_name;
    this.providerFullName = json.provider_full_name;
    this.providerDisplayName = json.provider_display_name;
    this.providerId = json.provider_id;
    this.providerInitials = json.provider_initials;
    this.providerProfilePictureUrl = json.provider_profile_photo_url;
    this.sessionType = json.session_type;
    this.startTime = new Date(json.start_time);
    this.videoEnabled = json.video_enabled;
    this.eventType = json.event_type;
    this.eventState = json.event_state;
    this.proposalStatus = json.proposal_status;
    this.locationId = json.location_id;
    this.locationName = json.location_name;
    this.title = json.title;
    this.patientIsNonSpnPatient = json.patient_is_non_spn_patient;
    this.patientAccountPending = json.patient_account_pending;
    this.userId = json.user_id;
    this.disallowClaims = json.disallow_claims;
    this.canceled = json.canceled;
    this.referralMemberState = json.referral_member_state;
    this.videoVendor = json.video_vendor;
    this.isFirst = json.is_first ?? false;
    this.riskPriority = json.risk_priority ?? null;
    this.recurring = json.recurring;
    this.recurringScheduleId = json.recurring_schedule_id;
    this.recurringType = json.recurring_type;
    this.recurringMonday = json.recurring_monday;
    this.recurringTuesday = json.recurring_tuesday;
    this.recurringWednesday = json.recurring_wednesday;
    this.recurringThursday = json.recurring_thursday;
    this.recurringFriday = json.recurring_friday;
    this.recurringSaturday = json.recurring_saturday;
    this.recurringSunday = json.recurring_sunday;
    this.recurringInterval = json.recurring_interval;
    this.recurringOrdinal = json.recurring_ordinal;
    this.recurringEnd = json.recurring_end;
    this.transcriptionRevokedConsentAt = json.transcription_revoked_consent_at;
    this.providerTranscriptionConsentAt = json.provider_transcription_consent_at;
    this.clientTranscriptionConsentAt = json.client_transcription_consent_at;
    this.providerTranscriptionRevokedConsentAt = json.provider_transcription_revoked_consent_at;
    this.clientTranscriptionRevokedConsentAt = json.client_transcription_revoked_consent_at;
    this.bothInSessionTime = json.both_in_session_time ?? 0;
    this.sessionOverlapStartedAt = json.session_overlap_started_at ?? '';
    this.sessionOverlapEndedAt = json.session_overlap_ended_at ?? '';
    this.sessionDurationTypeTag = extractSessionDurationTypeTagFromScheduledEventJson(json);
  }

  addOneSecond(dateString: string): Date {
    const dateTime = new Date(dateString);
    dateTime.setSeconds(dateTime.getSeconds() + 1);
    return dateTime;
  }

  get isAppointment(): boolean {
    return this.eventType === EventType.APPOINTMENT;
  }

  get isBlockedSession(): boolean {
    return this.eventType === EventType.BLOCKED;
  }

  get hasQuestionnaireScores(): boolean {
    return this.questionnaireScores && this.questionnaireScores.length > 0;
  }
}

// NOTE: (PE-1255) persona_client_id, persona_provider_id, patient_id may be null from backend
export interface ScheduledEventResponse {
  questionnaire_scores: IQuestionnaireScore[];
  patient_is_non_spn_patient: boolean;
  patient_account_pending: boolean;
  end_time: string;
  id: number;
  created_at: string;
  patient_first_name: string;
  patient_full_name: string;
  patient_display_name: string;
  patient_id: number;
  referral_member_id?: number;
  client_platform_identity?: string;
  patient_initials: string;
  patient_dob: string;
  persona_client_id: number;
  persona_provider_id: number;
  provider_first_name: string;
  provider_full_name: string;
  provider_display_name: string;
  provider_id: number;
  provider_initials: string;
  provider_profile_photo_url: string;
  session_type: string;
  start_time: string;
  video_enabled: boolean;
  event_type: string;
  event_state: string;
  proposal_status: string;
  location_id: number;
  location_name: string;
  title: string;
  user_id: number;
  disallow_claims: boolean;
  canceled: boolean;
  referral_member_state: string;
  video_vendor: VideoVendor;
  is_first?: boolean;
  risk_priority?: number | null;
  recurring?: boolean;
  recurring_schedule_id?: number;
  recurring_type?: string;
  recurring_monday?: boolean;
  recurring_tuesday?: boolean;
  recurring_wednesday?: boolean;
  recurring_thursday?: boolean;
  recurring_friday?: boolean;
  recurring_saturday?: boolean;
  recurring_sunday?: boolean;
  recurring_interval?: number;
  recurring_ordinal?: string;
  recurring_end?: string;
  transcription_revoked_consent_at?: string;
  client_transcription_consent_at?: string;
  provider_transcription_consent_at?: string;
  client_transcription_revoked_consent_at?: string;
  provider_transcription_revoked_consent_at?: string;
  both_in_session_time?: number; // The time both provider and client were in session
  session_overlap_started_at?: string; // The time the session overlap started
  session_overlap_ended_at?: string; // The time the session overlap ended
  session_duration_type_tag?: string;
  /** Some APIs nest the tag (e.g. AMS) instead of session_duration_type_tag. */
  session_duration_type?: string | { tag?: string };
}
